import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronRight, Truck, Shield, CheckCircle } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';

interface FormData {
  first_name: string; last_name: string; email: string; phone: string;
  address: string; city: string; state: string; postal_code: string; notes: string;
}

export default function CheckoutPage() {
  const { state, clearCart, subtotal } = useCart();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState<any>(null);
  const [form, setForm] = useState<FormData>({
    first_name: user?.name?.split(' ')[0] || '',
    last_name: user?.name?.split(' ').slice(1).join(' ') || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
    city: user?.city || '',
    state: '',
    postal_code: '',
    notes: '',
  });

  const shipping = subtotal >= 10000 ? 0 : 250;
  const total = subtotal + shipping;
  const formatPrice = (p: number) => `Rs.${p.toLocaleString()}`;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (state.items.length === 0) { toast.error('Your cart is empty'); return; }

    const required = ['first_name', 'last_name', 'email', 'phone', 'address', 'city'] as const;
    for (const field of required) {
      if (!form[field]) { toast.error(`Please fill in ${field.replace('_', ' ')}`); return; }
    }

    setLoading(true);
    try {
      const orderData = {
        ...form,
        items: state.items.map(item => ({
          product_id: item.productId,
          variant_id: item.variantId,
          quantity: item.quantity,
        })),
      };

      const res = await api.post('/orders', orderData);
      setOrderSuccess(res.data.order);
      clearCart();
      toast.success('Order placed successfully!');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  if (orderSuccess) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-20 text-center">
        <div className="flex justify-center mb-6">
          <CheckCircle size={80} className="text-green-500" />
        </div>
        <h1 className="text-3xl font-serif font-semibold text-gray-900 mb-3">Order Placed!</h1>
        <p className="text-gray-600 mb-2">Thank you for your order. We'll confirm it shortly.</p>
        <p className="text-sm text-gray-500 mb-6">
          Order Number: <span className="font-semibold text-gray-900">{orderSuccess.order_number}</span>
        </p>
        <div className="bg-gray-50 border border-gray-100 p-6 text-left rounded mb-8">
          <h3 className="font-semibold text-gray-900 mb-3">Order Summary</h3>
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex justify-between"><span>Payment Method</span><span className="font-medium">Cash on Delivery (COD)</span></div>
            <div className="flex justify-between"><span>Total Amount</span><span className="font-medium text-gray-900">{formatPrice(orderSuccess.total)}</span></div>
            <div className="flex justify-between"><span>Delivery Address</span><span className="font-medium">{orderSuccess.city}</span></div>
          </div>
        </div>
        <div className="flex gap-4 justify-center">
          {isAuthenticated && (
            <Link to="/account/orders" className="bg-gray-900 text-white px-8 py-3 text-sm font-medium uppercase tracking-wide">Track Order</Link>
          )}
          <Link to="/" className="border border-gray-900 text-gray-900 px-8 py-3 text-sm font-medium uppercase tracking-wide hover:bg-gray-900 hover:text-white transition-all">Continue Shopping</Link>
        </div>
      </div>
    );
  }

  if (state.items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-20 text-center">
        <h1 className="text-3xl font-serif font-semibold text-gray-900 mb-4">Your cart is empty</h1>
        <Link to="/" className="inline-block bg-gray-900 text-white px-8 py-3.5 text-sm font-medium uppercase tracking-wide">Continue Shopping</Link>
      </div>
    );
  }

  const pakistanCities = ['Lahore', 'Karachi', 'Islamabad', 'Rawalpindi', 'Faisalabad', 'Multan', 'Peshawar', 'Quetta', 'Sialkot', 'Gujranwala', 'Hyderabad', 'Bahawalpur', 'Sargodha', 'Sukkur', 'Larkana', 'Sheikhupura', 'Mirpur', 'Jhang', 'Rahim Yar Khan', 'Other'];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 md:py-12">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-xs text-gray-500 mb-8">
        <Link to="/" className="hover:text-gray-900">Home</Link>
        <ChevronRight size={14} />
        <Link to="/cart" className="hover:text-gray-900">Cart</Link>
        <ChevronRight size={14} />
        <span className="text-gray-900">Checkout</span>
      </nav>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Left - Form */}
          <div className="lg:col-span-7 space-y-8">
            <div>
              <h2 className="text-xl font-serif font-semibold text-gray-900 mb-6">Shipping Information</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="checkout-first-name" className="block text-xs font-medium text-gray-700 uppercase tracking-wider mb-1">First Name *</label>
                  <input id="checkout-first-name" name="first_name" value={form.first_name} onChange={handleChange} required className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-gray-900 transition-colors" />
                </div>
                <div>
                  <label htmlFor="checkout-last-name" className="block text-xs font-medium text-gray-700 uppercase tracking-wider mb-1">Last Name *</label>
                  <input id="checkout-last-name" name="last_name" value={form.last_name} onChange={handleChange} required className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-gray-900 transition-colors" />
                </div>
                <div>
                  <label htmlFor="checkout-email" className="block text-xs font-medium text-gray-700 uppercase tracking-wider mb-1">Email Address *</label>
                  <input id="checkout-email" name="email" type="email" value={form.email} onChange={handleChange} required className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-gray-900 transition-colors" />
                </div>
                <div>
                  <label htmlFor="checkout-phone" className="block text-xs font-medium text-gray-700 uppercase tracking-wider mb-1">Phone Number *</label>
                  <input id="checkout-phone" name="phone" type="tel" value={form.phone} onChange={handleChange} required placeholder="03XX-XXXXXXX" className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-gray-900 transition-colors" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="checkout-address" className="block text-xs font-medium text-gray-700 uppercase tracking-wider mb-1">Street Address *</label>
                  <input id="checkout-address" name="address" value={form.address} onChange={handleChange} required placeholder="House #, Street #, Area" className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-gray-900 transition-colors" />
                </div>
                <div>
                  <label htmlFor="checkout-city" className="block text-xs font-medium text-gray-700 uppercase tracking-wider mb-1">City *</label>
                  <select id="checkout-city" name="city" value={form.city} onChange={handleChange} required className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-gray-900 transition-colors bg-white">
                    <option value="">Select City</option>
                    {pakistanCities.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="checkout-province" className="block text-xs font-medium text-gray-700 uppercase tracking-wider mb-1">Province</label>
                  <select id="checkout-province" name="state" value={form.state} onChange={handleChange} className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-gray-900 transition-colors bg-white">
                    <option value="">Select Province</option>
                    {['Punjab', 'Sindh', 'KPK', 'Balochistan', 'AJK', 'GB', 'Islamabad Capital Territory'].map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="checkout-notes" className="block text-xs font-medium text-gray-700 uppercase tracking-wider mb-1">Order Notes (Optional)</label>
                  <textarea id="checkout-notes" name="notes" value={form.notes} onChange={handleChange} rows={3} placeholder="Any special instructions for delivery..." className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-gray-900 transition-colors resize-none" />
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div>
              <h2 className="text-xl font-serif font-semibold text-gray-900 mb-6">Payment Method</h2>
              <div className="border-2 border-gray-900 p-4 flex items-center gap-4">
                <div className="w-5 h-5 rounded-full border-2 border-gray-900 flex items-center justify-center">
                  <div className="w-2.5 h-2.5 rounded-full bg-gray-900" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Cash on Delivery (COD)</p>
                  <p className="text-xs text-gray-500 mt-0.5">Pay when your order arrives at your doorstep</p>
                </div>
                <div className="ml-auto text-2xl">💵</div>
              </div>
            </div>

            <div className="flex gap-4">
              <Truck size={20} className="text-green-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-900">Free delivery on orders above Rs. 10,000</p>
                <p className="text-xs text-gray-500">Delivery charge: Rs. 250 on orders below Rs. 10,000</p>
              </div>
            </div>
          </div>

          {/* Right - Order Summary */}
          <div className="lg:col-span-5">
            <div className="bg-gray-50 p-6 sticky top-24">
              <h2 className="text-xl font-serif font-semibold text-gray-900 mb-6">Order Summary</h2>

              <div className="space-y-4 mb-6">
                {state.items.map(item => (
                  <div key={`${item.productId}-${item.variantId}`} className="flex gap-3">
                    <div className="relative w-16 h-20 bg-white flex-shrink-0 border border-gray-100">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                      <span className="absolute -top-2 -right-2 w-5 h-5 bg-gray-900 text-white text-xs rounded-full flex items-center justify-center">{item.quantity}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 line-clamp-2">{item.name}</p>
                      {(item.size || item.color) && (
                        <p className="text-xs text-gray-500 mt-0.5">{[item.size, item.color].filter(Boolean).join(' / ')}</p>
                      )}
                      <p className="text-sm font-semibold text-gray-900 mt-1">{formatPrice(item.price * item.quantity)}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-200 pt-4 space-y-3">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Subtotal</span><span>{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Shipping</span>
                  <span className={shipping === 0 ? 'text-green-600 font-medium' : ''}>{shipping === 0 ? 'FREE' : formatPrice(shipping)}</span>
                </div>
                {shipping > 0 && <p className="text-xs text-gray-400">Add Rs.{(10000 - subtotal).toLocaleString()} more for free delivery</p>}
                <div className="flex justify-between text-base font-bold text-gray-900 border-t border-gray-200 pt-3">
                  <span>Total</span><span>{formatPrice(total)}</span>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-6 bg-gray-900 text-white py-4 text-sm font-semibold uppercase tracking-widest hover:bg-gray-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading ? <><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Placing Order...</> : 'Place Order'}
              </button>

              <div className="flex items-center justify-center gap-2 mt-4 text-xs text-gray-400">
                <Shield size={14} />
                <span>Your information is secure</span>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
